import { CurrencyPipe } from '@angular/common';
import { Inject, LOCALE_ID, Pipe, PipeTransform } from '@angular/core';

/**
 * Pipe wrapper sobre `CurrencyPipe` nativo do Angular.
 *
 * Originalmente criada para concatenar manualmente um prefixo `AR` antes do
 * `$` do Peso Argentino porque o CLDR `es-AR` colapsa ARS em `$` —
 * comportamento que era considerado indesejado. Em 2026-05-06 o tech lead
 * reverteu essa decisao: o simbolo de ARS volta a ser `$`, identico ao USD,
 * com diferenciacao por bandeira/icone + nome textual nas telas. A pipe
 * agora delega 100% das moedas ao `CurrencyPipe` nativo e existe apenas
 * para preservar a API dos templates (`{{ valor | moedaSimbolo: ... }}`)
 * sem exigir varredura de callsites.
 *
 * Comportamento: equivale a `{{ valor | currency: moeda : 'symbol' : digits : locale }}`,
 * com fallback para `0` quando `valor` for `null`/`undefined`/`NaN`.
 *
 * Spec de reversao: `vendas-site/specs/feat-peso-argentino/05-simbolo-cifrao.md`.
 */
@Pipe({
  name: 'moedaSimbolo'
})
export class MoedaSimboloPipe implements PipeTransform {
  private readonly currencyPipe: CurrencyPipe;

  constructor(@Inject(LOCALE_ID) locale: string) {
    this.currencyPipe = new CurrencyPipe(locale);
  }

  transform(
    valor: number | null | undefined,
    moeda: string,
    digits?: string,
    locale?: string
  ): string {
    if (valor == null || isNaN(valor as number)) {
      // Mantem fallback consistente com o pipe `currency` (que retorna '')
      // mas, no contexto deste PDV, sempre exibimos um numero — assumimos 0.
      return this.formatar(0, moeda, digits, locale);
    }
    return this.formatar(valor as number, moeda, digits, locale);
  }

  private formatar(
    valor: number,
    moeda: string,
    digits?: string,
    locale?: string
  ): string {
    // Delega 100% das moedas ao `CurrencyPipe` nativo. Para ARS com locale
    // `es-AR`, o CLDR produz `$ N,NN` — comportamento desejado apos a
    // reversao de 2026-05-06 (ver JSDoc da classe).
    const formatado = this.currencyPipe.transform(valor, moeda, 'symbol', digits, locale);
    return formatado ?? '';
  }
}
